import { Signal, computed, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, catchError, filter, map, of, switchMap, take } from 'rxjs';

/**
 * The cids of the feature flags, as the backend names them in App\FeatureFlag\FeatureFlags. Keep the two in step
 * (docs/FEATURE_FLAGS.md); never write a cid as a string elsewhere.
 */
export const FeatureFlag = {
  UPDATE_MAILS: 'update_mails',
  PRESS_CONTACT: 'press_contact',
} as const;

export type FeatureFlag = (typeof FeatureFlag)[keyof typeof FeatureFlag];

/** The answer of GET /api/feature-flags and GET /api/super-admin/feature-flags/on: the flags on for the caller. */
export interface FeatureFlagsOn {
  featureFlags: string[];
}

/** The flags on, and for whom: the `signedInAs` they were read for, or null while they are being read. */
interface FlagsState {
  who: string | null;
  flagsOn: ReadonlySet<string>;
}

const READING: FlagsState = { who: null, flagsOn: new Set() };

/**
 * The flags on for one part of the app (the site or superbeheer), read again each time signedInAs changes: on
 * sign-in and sign-out, the only moments the audience can change. Signed out of a part that has no visitors (read
 * returns null), every flag is off; so is every flag until the list is there (`whenKnown()` says when), and when it
 * cannot be read. Call it from a constructor (it needs the injection context).
 */
export class FeatureFlagsOnReader {
  /** Set in one place, so the flags and whom they are for never drift apart. */
  private readonly state = signal<FlagsState>(READING);
  private readonly state$ = toObservable(this.state);
  private readonly isOnPerFlag = new Map<FeatureFlag, Signal<boolean>>();

  private readonly signedInAs: Signal<string>;

  constructor(signedInAs: Signal<string>, read: (signedInAs: string) => Observable<FeatureFlagsOn> | null) {
    this.signedInAs = signedInAs;

    toObservable(signedInAs)
      .pipe(
        switchMap(who => {
          this.state.set(READING);

          const answer = read(who);

          if (answer === null) {
            this.state.set({ who, flagsOn: new Set() });

            return EMPTY;
          }

          // Inside switchMap, so a failed read leaves the flags off without ending the reading on the next sign-in.
          return answer.pipe(
            catchError(() => of<FeatureFlagsOn>({ featureFlags: [] })),
            map(list => ({ who, list })),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ who, list }) => this.state.set({ who, flagsOn: new Set(list.featureFlags) }));
  }

  /** One signal per flag, made once, so a template may call this on every check. */
  isOn(flag: FeatureFlag): Signal<boolean> {
    let isOn = this.isOnPerFlag.get(flag);

    if (isOn === undefined) {
      isOn = computed(() => this.state().flagsOn.has(flag));
      this.isOnPerFlag.set(flag, isOn);
    }

    return isOn;
  }

  /**
   * Completes once the list for whoever is signed in now is there, or there was nothing to read: for a route guard.
   * The filter reads the live signals, because the ReplaySubject may replay a state for a previous identity.
   */
  whenKnown(): Observable<void> {
    return this.state$.pipe(
      filter(() => this.state().who === this.signedInAs()),
      take(1),
      map(() => undefined),
    );
  }
}
